/** @file Normalizes a document curve to the rig code: 0 linear, 1 stepped, [x1, cy1, x2, cy2] bezier. */
import type { Curve } from '@pose2d/format/types';
import { clamp } from '@pose2d/math/clamp';

/** One segment's curve in rig form (§5.4): x normalized to [0, 1], cy absolute channel values. */
export type CurveCode = 0 | 1 | [number, number, number, number];

/**
 * `ease` y values scale the segment's change (cy = v0 + y·(v1 − v0)); `bezier` dv values offset the start value
 * (cy = v0 + dv, spec §5.2). x is clamped to [0, 1], which keeps x(s) monotonic (§6.4). Pass v0 = 0, v1 = 1
 * for channels that interpolate by progress (rgb, dark, deform; AR-4).
 */
export function compileCurve(curve: Curve | undefined, v0: number, v1: number): CurveCode {
  if (curve === undefined || curve === 'linear') return 0;
  if (curve === 'stepped') return 1;
  if ('ease' in curve) {
    const [x1, y1, x2, y2] = curve.ease;
    return [clamp(x1, 0, 1), v0 + y1 * (v1 - v0), clamp(x2, 0, 1), v0 + y2 * (v1 - v0)];
  }
  const [x1, d1, x2, d2] = curve.bezier;
  return [clamp(x1, 0, 1), v0 + d1, clamp(x2, 0, 1), v0 + d2];
}
